import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ITr069Devices,
  ITr069Field,
  ITr069Group,
  ITr069Status,
} from '@modules/clients/services/olt/models/onu-view.model';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';

/** Ícono de cada grupo: SmartOLT usa Font Awesome, aquí PrimeIcons. */
const GROUP_ICONS: Record<string, string> = {
  'fa-home': 'pi-home',
  'fa-forward': 'pi-directions',
  'fa-globe': 'pi-globe',
  'fa-sitemap': 'pi-sitemap',
  'fa-wifi': 'pi-wifi',
  'fa-desktop': 'pi-desktop',
  'fa-phone': 'pi-phone',
};

/** Nombre del grupo en español; los numerados ("Wireless LAN 2") conservan su número. */
const GROUP_NAMES: [RegExp, string][] = [
  [/^Port Forward$/, 'Redirección de puertos'],
  [/^IP Interface (.+)$/, 'Interfaz IP $1'],
  [/^LAN Ports$/, 'Puertos LAN'],
  [/^Wireless LAN (\d+)$/, 'WiFi $1'],
  [/^Hosts$/, 'Equipos conectados'],
  [/^Voice lines$/, 'Líneas de voz'],
  [/^Miscellaneous$/, 'Otros'],
];

const YES_NO: Record<string, string> = { Yes: 'Sí', No: 'No' };

/** Después de guardar, SmartOLT tarda en tener la lectura nueva del equipo. */
const REFRESH_AFTER_SAVE_MS = 2000;

/**
 * Panel TR-069 de la ONU, como el botón TR-069 de SmartOLT: lo que informa el equipo al ACS por
 * grupos (general, WAN, LAN, WiFi, hosts…), con la edición de los puertos LAN y del WiFi.
 * Si la ONU no tiene un perfil TR-069 solo se muestra cómo activarlo.
 */
@Component({
  selector: 'app-onu-tr069',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, FormsModule, ButtonModule],
  templateUrl: './onu-tr069.component.html',
})
export class OnuTr069Component implements OnChanges, OnDestroy {
  @Input({ required: true }) onuId!: number;
  /** la ONU tiene un perfil TR-069 (Mgmt IP / TR069 Profile) */
  @Input() enabled = false;

  public status: ITr069Status | null = null;
  public loading = false;
  public error = '';
  public open = new Set<string>(['General']);
  public refreshing = new Set<string>();
  /** cambios sin aplicar: OID → valor nuevo */
  public changes: Record<string, string> = {};
  public invalid: Record<string, string> = {};
  /** también guardarlo en SmartOLT para reprovisionar el WiFi tras un reset de fábrica */
  public rememberWifi = true;
  public saving = false;
  public revealed = new Set<string>();
  public devices: Record<string, ITr069Devices | 'loading' | 'error'> = {};

  private refreshTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly onuViewService: OnuViewService,
    private readonly toastService: ToastrService,
    private readonly confirmationService: ConfirmationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['onuId'] || changes['enabled']) && this.enabled) this.load();
  }

  ngOnDestroy(): void {
    clearTimeout(this.refreshTimer);
  }

  /**
   * load: el panel completo (lo que el ACS tiene del equipo)
   */
  public async load() {
    this.loading = true;
    this.error = '';
    try {
      const response = await this.onuViewService.read<ITr069Status>(this.onuId, 'tr069');
      this.status = response.data;
      this.changes = {};
      this.invalid = {};
      this.devices = {};
    } catch (error) {
      this.error = 'No se pudo leer el equipo por TR-069.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * refreshGroup: pide al equipo los valores de un grupo (el refresh de cada grupo en SmartOLT)
   */
  public async refreshGroup(name: string) {
    if (!this.status || this.refreshing.has(name)) return;
    this.refreshing.add(name);
    try {
      const response = await this.onuViewService.read<ITr069Status>(this.onuId, 'tr069', {
        group: name,
      });
      const data = response.data;
      const fresh = data?.groups?.[0];
      if (data?.status === 'countdown' || data?.status === 'reload' || !fresh) {
        this.toastService.info(
          data?.message ?? 'El equipo todavía no respondió; inténtelo en unos segundos.',
        );
        return;
      }
      this.status.groups = this.status.groups.map((g) =>
        g.name === name ? { ...fresh, icon: g.icon } : g,
      );
      this.discardGroup(fresh);
    } catch (error) {
      this.toastService.error('No se pudo refrescar el grupo.');
    } finally {
      this.refreshing.delete(name);
    }
  }

  public toggle(name: string) {
    if (this.open.has(name)) this.open.delete(name);
    else this.open.add(name);
  }

  // ---- edición ----
  public valueOf(field: ITr069Field): string {
    return this.changes[field.oid] ?? field.value;
  }

  public change(field: ITr069Field, value: string) {
    if (value === field.value) {
      delete this.changes[field.oid];
      delete this.invalid[field.oid];
      return;
    }
    this.changes[field.oid] = value;
    if (field.valid && !new RegExp(field.valid).test(value)) {
      this.invalid[field.oid] = field.validMessage || 'Valor no válido para este parámetro';
    } else {
      delete this.invalid[field.oid];
    }
  }

  public get changeCount(): number {
    return Object.keys(this.changes).length;
  }

  public get hasInvalid(): boolean {
    return Object.keys(this.invalid).length > 0;
  }

  public discard() {
    this.changes = {};
    this.invalid = {};
  }

  /**
   * apply: "Apply changes" de SmartOLT, con confirmación
   */
  public apply() {
    if (!this.changeCount || this.hasInvalid || this.saving) return;
    this.confirmationService.confirm({
      header: 'Aplicar cambios TR-069',
      message: `Se enviarán ${this.changeCount} cambio(s) al equipo. Si cambia el WiFi, los equipos conectados se desconectarán. ¿Continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, aplicar',
      rejectLabel: 'Cancelar',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.save(),
    });
  }

  private async save() {
    const params = { ...this.changes };
    const touched = this.groupsWith(Object.keys(params));
    this.saving = true;
    try {
      const response = await this.onuViewService.action(this.onuId, 'tr069_save', {
        params,
        remember: this.rememberWifi ? this.wifiOids(params) : [],
      });
      this.toastService.success(response.message ?? 'Cambios enviados al equipo');
      this.changes = {};
      // como SmartOLT: se vuelve a leer lo que cambió cuando el equipo ya lo tiene
      this.refreshTimer = setTimeout(
        () => touched.forEach((name) => this.refreshGroup(name)),
        REFRESH_AFTER_SAVE_MS,
      );
    } catch (error: any) {
      this.toastService.error(error?.error?.message ?? 'SmartOLT rechazó los cambios');
    } finally {
      this.saving = false;
    }
  }

  private wifiOids(params: Record<string, string>): string[] {
    return this.allFields()
      .filter((f) => f.remember === 'wifi' && params[f.oid] !== undefined)
      .map((f) => f.oid);
  }

  private groupsWith(oids: string[]): string[] {
    return (this.status?.groups ?? [])
      .filter((g) => this.fieldsOf(g).some((f) => oids.includes(f.oid)))
      .map((g) => g.name);
  }

  /** Al refrescar un grupo sus cambios sin aplicar quedan sin sentido. */
  private discardGroup(group: ITr069Group) {
    this.fieldsOf(group).forEach((f) => {
      delete this.changes[f.oid];
      delete this.invalid[f.oid];
    });
  }

  private allFields(): ITr069Field[] {
    return (this.status?.groups ?? []).flatMap((g) => this.fieldsOf(g));
  }

  private fieldsOf(group: ITr069Group): ITr069Field[] {
    return group.layout === 'table' ? group.rows.flat() : group.fields;
  }

  // ---- equipos conectados al WiFi ----
  public async toggleDevices(oid: string) {
    if (this.devices[oid] && this.devices[oid] !== 'error') {
      delete this.devices[oid];
      return;
    }
    this.devices[oid] = 'loading';
    try {
      const response = await this.onuViewService.read<ITr069Devices>(this.onuId, 'tr069_devices', {
        oid,
      });
      this.devices[oid] = response.data?.ok ? response.data : 'error';
    } catch (error) {
      this.devices[oid] = 'error';
    }
  }

  public devicesOf(oid: string): ITr069Devices | null {
    const value = this.devices[oid];
    return typeof value === 'object' ? value : null;
  }

  // ---- presentación ----
  public groupName(name: string): string {
    const match = GROUP_NAMES.find(([re]) => re.test(name));
    return match ? name.replace(match[0], match[1]) : name;
  }

  public groupIcon(group: ITr069Group): string {
    return GROUP_ICONS[group.icon] ?? 'pi-cog';
  }

  public optionLabel(label: string): string {
    return YES_NO[label] ?? label;
  }

  /** Valor de un campo que no se edita: Yes/No en español, vacío como guion. */
  public display(field: ITr069Field): string {
    const option = field.options.find((o) => o.value === field.value);
    const text = option ? option.label : field.display || field.value;
    return this.optionLabel(text) || '–';
  }

  public toggleReveal(oid: string) {
    if (this.revealed.has(oid)) this.revealed.delete(oid);
    else this.revealed.add(oid);
  }

  public changedIn(group: ITr069Group): number {
    return this.fieldsOf(group).filter((f) => this.changes[f.oid] !== undefined).length;
  }
}
